import { inject, type InjectionKey } from 'vue'

import type { AccountService } from '@/services/account/account.service.js'
import type { AnalyticsService } from '@/services/analytics/analytics.service.js'
import type { AppCatalogService } from '@/services/app-catalog/app-catalog.service.js'
import type { AuditService } from '@/services/audit/audit.service.js'
import type { ShopEventService } from '@/services/shop-event/shop-event.service.js'
import type { AuthService } from '@/services/auth/auth.service.js'
import type { OperatorService } from '@/services/operator/operator.service.js'
import type { OverviewService } from '@/services/overview/overview.service.js'
import type { PlanService } from '@/services/plan/plan.service.js'
import type { PromotionService } from '@/services/promotion/promotion.service.js'
import type { ReportService } from '@/services/report/report.service.js'
import type { RevenueService } from '@/services/revenue/revenue.service.js'
import type { RoleService } from '@/services/role/role.service.js'
import type { ShopService } from '@/services/shop/shop.service.js'
import type { ShopifyFunctionService } from '@/services/shopify-function/shopify-function.service.js'
import type { SubscriptionService } from '@/services/subscription/subscription.service.js'
import type { TranslationService } from '@/services/translation/translation.service.js'
import type { WidgetService } from '@/services/widget/widget.service.js'

/**
 * Every service the app provides, in one injectable container.
 *
 * ## Why injection rather than a module singleton
 *
 * A service needs an `HttpClient`, and the client needs the base URL and a set of
 * interceptors. Exactly one place may know all three — `bootstrap.ts` — because that is
 * what keeps `fetch`, the base URL and the 401 handler out of every other module, and what
 * lets a test build a store against a fake client with no environment and no `fetch`.
 *
 * A module-level singleton would read the environment at import time instead, making every
 * module that transitively imports a service un-importable without a complete `.env`. That
 * is not a hypothetical failure: the deleted BFF's logger did it, and it broke every test
 * that merely imported the application graph.
 *
 * The imports here are **type-only**, so this file adds no runtime edge from `types/` to
 * `services/` — it names a contract, it does not reach for an implementation.
 */
export interface ServiceContainer {
  readonly auth: AuthService
  readonly account: AccountService
  readonly operator: OperatorService
  readonly role: RoleService
  readonly audit: AuditService
  readonly shopEvent: ShopEventService
  readonly shop: ShopService
  readonly promotion: PromotionService
  readonly appCatalog: AppCatalogService
  readonly revenue: RevenueService
  readonly analytics: AnalyticsService
  readonly widget: WidgetService
  readonly plan: PlanService
  readonly subscription: SubscriptionService
  readonly shopifyFunction: ShopifyFunctionService
  readonly overview: OverviewService
  readonly translation: TranslationService
  readonly report: ReportService
}

export const SERVICES = Symbol('zapio.services') as InjectionKey<ServiceContainer>

/**
 * The container, or a usable error.
 *
 * `inject()` returns `undefined` when nothing provided the key, and Vue logs a warning that
 * is easy to miss in a console full of HMR noise. The next line would then be
 * `Cannot read properties of undefined (reading 'auth')` from inside a store action — a
 * message that points at the store rather than at the missing `app.provide` in bootstrap.
 *
 * Verified: this works inside a Pinia **setup store** even when the store is first used
 * before `app.mount()`, which is exactly what `bootstrap.ts` does when it hydrates the
 * session. Pinia runs a setup store inside the app's context for precisely this reason.
 */
export function useServices(): ServiceContainer {
  /**
   * Checked for `undefined` as well as the default, and the cast is why.
   *
   * `inject()` returns its default when a key was never provided — but when it is called
   * **outside a setup context altogether** it warns to the console and returns `undefined`,
   * ignoring the default entirely. Its signature does not say so, so a check against the
   * default alone typechecks, passes review, and lets a store be built with no services at
   * all. The next action then fails with `Cannot read properties of undefined (reading
   * 'auth')` — precisely the message this function exists to replace. A test that
   * constructed a store with no app is what found it.
   */
  // `?? null` normalises the two "nothing here" answers into one, and it is not noise.
  // Neither a widening annotation nor an `as` cast survives: the compiler narrows this
  // expression back to `ServiceContainer | null` from `inject`'s own signature, and then
  // reports an explicit `=== undefined` check as having no overlap. `??` is the one form
  // that is honest at runtime and accepted by the type system.
  const services = inject(SERVICES, null) ?? null

  if (services === null) {
    throw new Error(
      'No ServiceContainer is reachable. Either bootstrap.ts did not call ' +
        'app.provide(SERVICES, …) before this store was first used, or the store was ' +
        'constructed outside any app context — a test must install a Pinia on an app that ' +
        'provides its own container.',
    )
  }

  return services
}
